import { useEffect, useState } from 'react';
import { Icon, Input, Label, Select } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import clsx from 'clsx';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { GuarantorRoles } from '../Guarantors/GuarantorRoles/GuarantorRoles';
import {
  TRole,
  TSignupFormDataDE,
} from '../NewSignupFormDE/NewSignupFormDE.types';
import signupFormStyles from '../SignupForm/SignupForm.module.css';
import { AddressFieldsDE } from './AddressFieldsDE/AddressFieldsDE';
import styles from './PersonalDetailsDE.module.css';

type TProps = {
  roleType: TRole;
  index?: number;
};

export const PersonalDetailsDE = ({ roleType, index = 0 }: TProps) => {
  const [secondaryPhoneNumberIsVisible, setSecondaryPhoneNumberIsVisible] =
    useState(false);

  const { trackEvent } = useAmplitude();

  const {
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<TSignupFormDataDE>();

  const PERSON_PROPERTY =
    roleType === 'applicant'
      ? (`${roleType}` as const)
      : (`${roleType}.${index}` as const);

  const {
    fields: addresses,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({
    name: `${PERSON_PROPERTY}.residentialAddresses`,
  });

  const ERROR_INDEX_PROPERTY =
    roleType === 'applicant' ? errors.applicant : errors?.[roleType]?.[index];

  const hasAddressError =
    ERROR_INDEX_PROPERTY?.residentialAddresses?.[0]?.postcode?.message ||
    ERROR_INDEX_PROPERTY?.residentialAddresses?.[0]?.town?.message;

  const { checkIntroducerPermission } = useIntroducerContext();
  const isDisabled = !checkIntroducerPermission('can_create_applications');

  useEffect(() => {
    if (getValues(`${PERSON_PROPERTY}.roles`)) return;

    if (roleType === 'applicant') {
      setValue(`${PERSON_PROPERTY}.roles`, [
        'applicant',
        'guarantor',
        'director',
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={signupFormStyles.cardContents}>
      {roleType === 'guarantor' && <GuarantorRoles index={index} />}
      <div className={signupFormStyles.formField}>
        <Label className={styles.label} htmlFor={`${PERSON_PROPERTY}.title`}>
          <Trans>Name</Trans>
        </Label>
        <div className={styles.nameContainer}>
          <Select
            {...register(`${PERSON_PROPERTY}.title`)}
            className={styles.title}
            type="text"
            id={`${PERSON_PROPERTY}.title`}
            placeholder={t`Title`}
            error={ERROR_INDEX_PROPERTY?.title?.message}
            disabled={isDisabled}
            aria-disabled={isDisabled}
          >
            <option value="herr">
              <Trans>Mr</Trans>
            </option>
            <option value="frau">
              <Trans>Ms</Trans>
            </option>
          </Select>
          <Input
            {...register(`${PERSON_PROPERTY}.firstName`)}
            type="text"
            data-testid="first-name"
            placeholder={t`First name`}
            error={ERROR_INDEX_PROPERTY?.firstName?.message}
            disabled={isDisabled}
            aria-disabled={isDisabled}
          />
          <Input
            {...register(`${PERSON_PROPERTY}.lastName`)}
            type="text"
            data-testid="last-name"
            placeholder={t`Last name`}
            error={ERROR_INDEX_PROPERTY?.lastName?.message}
            disabled={isDisabled}
            aria-disabled={isDisabled}
          />
        </div>
      </div>
      <div className={signupFormStyles.formField}>
        <Label className={styles.label} htmlFor={`${PERSON_PROPERTY}.dobDay`}>
          <Trans>Date of birth</Trans>
        </Label>
        <div className={styles.dobContainer}>
          <Input
            {...register(`${PERSON_PROPERTY}.dobDay`)}
            className={styles.dobDay}
            type="text"
            data-testid="dob-day"
            placeholder="TT"
            id={`${PERSON_PROPERTY}.dobDay`}
            error={ERROR_INDEX_PROPERTY?.dobDay?.message}
            disabled={isDisabled}
            aria-disabled={isDisabled}
          />
          <Input
            {...register(`${PERSON_PROPERTY}.dobMonth`)}
            type="text"
            data-testid="dob-month"
            placeholder="MM"
            error={ERROR_INDEX_PROPERTY?.dobMonth?.message}
            disabled={isDisabled}
            aria-disabled={isDisabled}
          />
          <Input
            {...register(`${PERSON_PROPERTY}.dobYear`)}
            type="text"
            data-testid="dob-year"
            placeholder="JJJJ"
            error={ERROR_INDEX_PROPERTY?.dobYear?.message}
            disabled={isDisabled}
            aria-disabled={isDisabled}
          />
        </div>
      </div>
      <div className={signupFormStyles.formField}>
        <Label
          className={styles.label}
          htmlFor={`${PERSON_PROPERTY}.phoneNumber`}
        >
          <Trans>Phone number</Trans>
        </Label>
        <Input
          {...register(`${PERSON_PROPERTY}.phoneNumber`)}
          id={`${PERSON_PROPERTY}.phoneNumber`}
          placeholder="z.B. 01522 3433333"
          error={ERROR_INDEX_PROPERTY?.phoneNumber?.message}
          disabled={isDisabled}
          aria-disabled={isDisabled}
        />
        {!secondaryPhoneNumberIsVisible && (
          <ButtonLink
            type="button"
            className={clsx(styles.addNumberButton, {
              [styles.addNumberButtonWithError]:
                ERROR_INDEX_PROPERTY?.phoneNumber?.message,
            })}
            onClick={() => {
              setSecondaryPhoneNumberIsVisible(true);
              trackEvent(AMPLITUDE_EVENTS.extraDetailsAdded, {
                'Details added': 'Phone number',
                'CTA text': 'Add another number',
              });
            }}
            disabled={isDisabled}
            aria-disabled={isDisabled}
          >
            <Icon icon="plus" />
            <Trans>Add another number</Trans>
          </ButtonLink>
        )}
      </div>
      {secondaryPhoneNumberIsVisible && (
        <div className={signupFormStyles.formField}>
          <div className={styles.secondaryNumberContainer}>
            <Input
              {...register(`${PERSON_PROPERTY}.secondaryPhoneNumber`)}
              data-testid="secondary-number-input"
              type="text"
              error={ERROR_INDEX_PROPERTY?.secondaryPhoneNumber?.message}
              placeholder="z.B. 01522 3433333"
              disabled={isDisabled}
              aria-disabled={isDisabled}
            />
            <ButtonLink
              type="button"
              className={styles.secondaryNumberCancel}
              data-testid="secondary-number-cancel-btn"
              onClick={() => {
                setSecondaryPhoneNumberIsVisible(false);
                trackEvent(AMPLITUDE_EVENTS.extraDetailsRemoved, {
                  'Details removed': 'Phone number',
                  'CTA text': '',
                });
              }}
              disabled={isDisabled}
              aria-disabled={isDisabled}
            >
              <Icon height={25} width={25} icon="plus" />
            </ButtonLink>
          </div>
        </div>
      )}
      <div className={signupFormStyles.formField}>
        <Input
          {...register(`${PERSON_PROPERTY}.email`)}
          label={t`Personal email address`}
          placeholder="z.B. vorname@firmenname.de"
          error={ERROR_INDEX_PROPERTY?.email?.message}
          disabled={isDisabled}
          aria-disabled={isDisabled}
        />
      </div>
      <AddressFieldsDE
        roleType={roleType}
        addressIndex={0}
        personIndex={index}
        disabled={isDisabled}
        aria-disabled={isDisabled}
      />
      {!addresses[1] && (
        <ButtonLink
          type="button"
          onClick={() => {
            appendAddress({ postcode: undefined });
            trackEvent(AMPLITUDE_EVENTS.extraDetailsAdded, {
              'Details added': 'Address',
              'CTA text': 'Add address',
            });
          }}
          className={clsx(styles.addAddressButton, {
            [styles.addAddressButtonWithError]: hasAddressError,
          })}
          disabled={isDisabled}
          aria-disabled={isDisabled}
        >
          <Icon icon="plus" />
          <Trans>Add address</Trans>
        </ButtonLink>
      )}
      {addresses[1] && (
        <div className={styles.secondaryAddressContainer}>
          <AddressFieldsDE
            roleType={roleType}
            addressIndex={1}
            personIndex={index}
            disabled={isDisabled}
            aria-disabled={isDisabled}
          />
          <ButtonLink
            type="button"
            className={styles.secondaryAddressCancel}
            data-testid="secondary-address-cancel-btn"
            onClick={() => {
              removeAddress(1);
              trackEvent(AMPLITUDE_EVENTS.extraDetailsRemoved, {
                'Details removed': 'Address',
                'CTA text': '',
              });
            }}
          >
            <Icon height={25} width={25} icon="plus" />
          </ButtonLink>
        </div>
      )}
    </div>
  );
};
